<template>

  <v-container class="pa-0 ma-0 route-order-action-card">
    <!------------------------------------------------>
    <!-- Floating Step Index (for remaining orders) -->
    <!------------------------------------------------>
    <div class="order-index" v-if="orderIndexForDisplay">
      <span :style="(isReorderMode) ? 'font-weight:500;color:#f8f8f8;' : 'font-weight:400;color:#828282;'">
        {{ orderIndexForDisplay }}
      </span>
    </div>

    <v-row :class="{ 'can-move': isReorderMode && canMove }" class="order-card-container mt-4 mb-4 pb-1">
      <!--------------------------------------------->
      <!-- Order Number & Brand, Shipper, Receiver -->
      <!--------------------------------------------->
      <v-col cols="12" xs="12" sm="9" md="9" lg="9">
        <v-icon left color="yellow darken-3" class="pb-1">mdi-package-variant-closed</v-icon>
        <router-link :to="generateOrderInfoPageLink(order)">
          <span class="order-number">{{ order.thirdparty_order_number }}</span>
        </router-link>
        <span class="brand-name" style="padding-left:8px;padding-right:8px;"> - </span>
        <span class="brand-name">{{ order.thirdparty_brand_name }}</span>

        <div class="mt-4 d-flex align-center">
          <router-link :to="generateLocationPageLink(order.shipper)">{{ order.shipper.name }}</router-link>
          <v-icon color="#727272" class="px-2">mdi-arrow-right-thin</v-icon>
          <router-link :to="generateLocationPageLink(order.receiver)">{{ order.receiver.name }}</router-link>
        </div>

        <div class="d-flex align-center mt-4" v-if="showEstPickupTime || showEstDropoffTime">
          <template v-if="showEstPickupTime">
            <div class="mr-2 arrival-time-label">Pickup:</div>
            <div class="mr-9 arrival-time">{{ estimatedPickupTimeForDisplay }}</div>
          </template>
          <template v-if="showEstDropoffTime">
            <div class="mr-2 arrival-time-label">Dropoff:</div>
            <div class="arrival-time">{{ estimatedDropoffTimeForDisplay }}</div>
          </template>
        </div>
      </v-col>

      <!---------------------->
      <!-- Status & Actions -->
      <!---------------------->
      <v-col cols="12" xs="12" sm="3" md="3" lg="3" class="d-flex flex-column align-center justify-center">
        <!-- Status -->
        <v-chip label small outlined :class="order.order_status">{{ order.order_status }}</v-chip>

        <!-- Completed date -->
        <div class="mt-4 d-flex align-center" v-if="showCompletedTime">
          <div class="mr-1 completed-time-label">at:</div>
          <div class="completed-time">{{ completedTimeForDisplay }}</div>
        </div>

        <!-- Action menu (with loading graphic and error display) -->
        <template v-if="!hideActions && (canRescheduleOrder || canCancelOrder)">
          <v-progress-circular indeterminate :size="24" class="mt-4" color="primary" v-if="isUpdating" />

          <v-tooltip top v-else-if="updatingError">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                  color="error"
                  class="mt-5"
                  style="font-size:26px;cursor:pointer;"
                  v-bind="attrs"
                  v-on="on">
                mdi-alert
              </v-icon>
            </template>
            <div>{{ updatingError }}</div>
          </v-tooltip>

          <v-menu offset-y class="delivery-order-action-menu" v-else>
            <template v-slot:activator="{ on, attrs }">
              <v-btn plain v-bind="attrs" v-on="on" class="mt-4 px-2" :disabled="isReorderMode">
                Actions
                <v-icon right class="pt-1">mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list class="route-card-action-menu-list">
              <v-list-item class="list-item" v-if="canRescheduleOrder">
                <dialog-reschedule-order-button
                    buttonStyle="menu-item"
                    buttonText="Reschedule Order"
                    :buttonWidth="168"
                    :fullscreen="isMobileScreen"
                    :order="order"
                    :requiresRouteChange="requiresRouteChange"
                    v-on:confirmed="emitRescheduleOrder" />
              </v-list-item>
              <v-list-item class="list-item" v-if="canCancelOrder">
                <dialog-cancel-order-button
                    buttonStyle="menu-item"
                    buttonText="Cancel Order"
                    :buttonWidth="168"
                    :fullscreen="isMobileScreen"
                    :order="order"
                    @confirmed="emitCancelOrder" />
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-col>
    </v-row>
  </v-container>

</template>

<script>
import DialogRescheduleOrderButton from '@/components/orders/DialogRescheduleOrderButton'
import DialogCancelOrderButton from '@/components/orders/DialogCancelOrderButton'

export default {
  name: 'RouteOrderActionCard',

  components: {
    DialogRescheduleOrderButton,
    DialogCancelOrderButton
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    requiresRouteChange: {
      type: Boolean,
      default: false
    },
    hideActions: {
      type: Boolean,
      default: false
    },
    isReorderMode: {
      type: Boolean,
      default: false
    },
    canMove: {
      type: Boolean,
      default: true
    },
    canRescheduleInTransit: {
      type: Boolean,
      default: false
    },
    isUpdating: {
      type: Boolean,
      default: false
    },
    updatingError: {
      type: String,
      default: null
    },
    orderIndex: {
      type: Number,
      default: 0
    }
  },

  data () {
    return {
      isMobileScreen: window.innerWidth < 768
    }
  },

  computed: {
    // Determines if the provided order can be re-scheduled or not
    canRescheduleOrder () {
      const baseConditions = this.order.order_status !== 'BEING_SCHEDULED' &&
        this.order.order_status !== 'CANCELED' &&
        this.order.order_status !== 'RESCHEDULED' &&
        this.order.order_status !== 'DELIVERED' &&
        this.order.order_status !== 'REJECTED_ON_DELIVERY'

      // Check if in transit (to or at location) rescheduling is permitted
      if (!this.canRescheduleInTransit) {
        return baseConditions &&
          this.order.order_status !== 'TO_SHIPPER' &&
          this.order.order_status !== 'AT_SHIPPER' &&
          this.order.order_status !== 'TO_RECEIVER' &&
          this.order.order_status !== 'AT_RECEIVER'
      }

      return baseConditions
    },
    // Determines if the provided order can be canceled or not
    canCancelOrder () {
      return false // Not yet supported !!!
      // return this.order.order_status !== 'BEING_SCHEDULED' &&
      //   this.order.order_status !== 'PICKED_UP' &&
      //   this.order.order_status !== 'CANCELED' &&
      //   this.order.order_status !== 'RESCHEDULED' &&
      //   this.order.order_status !== 'DELIVERED' &&
      //   this.order.order_status !== 'REJECTED_ON_DELIVERY'
    },
    orderIndexForDisplay () {
      return (this.order.estimated_receiver_arrival_time)
        ? this.orderIndex
        : null
    },
    showCompletedTime () {
      return this.order.order_status === 'DELIVERED' ||
        this.order.order_status === 'RESCHEDULED' ||
        this.order.order_status === 'REJECTED_ON_DELIVERY' ||
        this.order.order_status === 'CANCELED'
    },
    showEstPickupTime () {
      return this.order.order_status === 'SCHEDULED' ||
        this.order.order_status === 'TO_SHIPPER'
    },
    showEstDropoffTime () {
      return this.order.order_status === 'SCHEDULED' ||
        this.order.order_status === 'TO_SHIPPER' ||
        this.order.order_status === 'AT_SHIPPER' ||
        this.order.order_status === 'PICKED_UP' ||
        this.order.order_status === 'TO_RECEIVER'
    },
    completedTimeForDisplay () {
      let completedTime = null
      switch (this.order.order_status) {
        case 'DELIVERED': completedTime = this.order.delivered_at; break
        case 'RESCHEDULED': completedTime = this.order.rescheduled_at; break
        case 'REJECTED_ON_DELIVERY': completedTime = this.order.rejected_at; break
        case 'CANCELED': completedTime = this.order.canceled_at; break
      }
      return (completedTime)
        ? this.$root.utils.dateFormat(completedTime, 'h:mm a')
        : ''
    },
    estimatedPickupTimeForDisplay () {
      const arrivalTime = this.order.estimated_shipper_arrival_time
      return (arrivalTime)
        ? this.$root.utils.dateFormat(arrivalTime, 'h:mm a')
        : '-'
    },
    estimatedDropoffTimeForDisplay () {
      const arrivalTime = this.order.estimated_receiver_arrival_time
      return (arrivalTime)
        ? this.$root.utils.dateFormat(arrivalTime, 'h:mm a')
        : '-'
    }
  },

  mounted () {
    // Initialize viewport detection
    this.handleResize = () => {
      this.isMobileScreen = window.innerWidth < 768
    }
    window.addEventListener('resize', this.handleResize)
    this.isMobileScreen = window.innerWidth < 768 // set initial value
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    // Generate link data for order info page
    generateOrderInfoPageLink (order) {
      if (!order || !order.model) return ''
      const latestOrder = (order.order_history && order.order_history.length > 1)
        ? order.order_history[order.order_history.length - 1]
        : order

      return {
        name: 'order-info-overview',
        params: { orderId: latestOrder.id }
      }
    },

    // Generate link data for shipper/receiver page (for a provided location item)
    generateLocationPageLink (loc) {
      if (loc && loc.model) {
        if (loc.model === 'Shipper') {
          return {
            name: 'shipper-item',
            params: { shipperId: loc.id }
          }
        } else if (loc.model === 'Receiver') {
          return {
            name: 'receiver-item',
            params: { receiverId: loc.id }
          }
        } else {
          return ''
        }
      } else {
        return ''
      }
    },

    // Emit reschedule order action
    // data.order              = the order to reschedule
    // data.notes              = reason for reschedule (optional)
    // data.notes_route_change = route change notes (optional)
    // data.client_coords      = the client coords for re-route (optional)
    emitRescheduleOrder (data = {}) {
      this.$emit('rescheduleOrder', data)
    },

    // Emit cancel order action
    // data.order = the order to cancel
    // data.notes = notes (optional)
    emitCancelOrder (data = {}) {
      this.$emit('cancelOrder', data)
    }
  }
}

</script>

<style scoped>
  .route-order-action-card {
    position: relative;
  }

  .route-order-action-card .order-index {
    position: absolute;
    left: -33px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.1rem;
  }

  .route-order-action-card .order-card-container {
    border: 1px solid #525252;
    border-radius: 4px;
  }

  .route-order-action-card .order-card-container.can-move {
    border: 1px solid #dfdfdf;
    border-radius: 4px;
    background-color: #292929;
  }

  .route-order-action-card .attr-label {
    color: #9c9c9c;
    font-weight: 400;
    width: 150px;
  }

  .route-order-action-card .order-number {
    font-size: 1.2rem;
    font-weight: 500;
  }

  .route-order-action-card .brand-name {
    color: #9c9c9c;
    font-size: 1.14rem;
    font-weight: 400;
  }

  .route-order-action-card .completed-time-label {
    color: #7c7c7c;
    font-size: 0.9rem;
  }

  .route-order-action-card .completed-time {
    color: #c9c9c9;
    font-size: 1.0rem;
    font-weight: 500;
  }

  .route-order-action-card .arrival-time-label {
    color: #7c7c7c;
    font-size: 1.0rem;
  }

  .route-order-action-card .arrival-time {
    color: #c9c9c9;
    font-size: 1.08rem;
    font-weight: 500;
  }

  .route-card-action-menu-list {
    background-color: #323232 !important;
  }

  .route-card-action-menu-list .list-item {
    margin: 0;
    padding: 0;
  }

  .route-order-action-card a {
    text-decoration: none;
  }
  .route-order-action-card a:hover {
    text-decoration: underline;
  }
</style>
